import { Stack, Text } from "@certn/cascadia";
import {
  ColumnDefinition,
  DataTable,
  HoverInfo,
  Pagination,
  StatusTag,
} from "@certn/cascadia-react";
import { useEffect, useMemo, useState } from "react";
import type { Key, Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { FilterBar } from "../../../components/filters/FilterBar";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { formatMonitoringDate } from "../monitoringFormat";
import { getPendingResultTooltipMessage } from "../monitoringRunChangeText";
import type { DashboardApplicant, MonitoredCheckTypeSummary } from "../types";
import { getApplicantDisplayName } from "../types";
import { useDashboardApplicants } from "../useMonitoringApi";
import { MonitoringBulkActionBar } from "./MonitoringBulkActionBar";
import { MonitoringCheckTypeFilter } from "./MonitoringCheckTypeFilter";
import { MonitoringResultFilter } from "./MonitoringResultFilter";
import { MonitoringRunResultTagWithTooltip } from "@certn/monitoring";
import { MonitoringStatusFilter } from "./MonitoringStatusFilter";
import { MonitoringSummaryTiles } from "./MonitoringSummaryTiles";
import { MonitorStatusTag } from "./monitoringTags";

function PendingResultTag({
  monitorStatus,
  nextScheduledRunDate,
}: {
  monitorStatus: string | null;
  nextScheduledRunDate: string | null;
}) {
  const { t } = useTranslation();
  const description = getPendingResultTooltipMessage(
    monitorStatus,
    nextScheduledRunDate,
    formatMonitoringDate,
    t
  );
  return (
    <HoverInfo
      placement="top"
      testID="result-pending-tooltip"
      infoContent={<Text style="form/body-secondary">{description}</Text>}
      trigger={
        <StatusTag
          leftIcon="schedule"
          fill="background-accent-neutral-weak"
          color="icon-base"
          statusInfoIcon={false}
          testID="result-pending"
        >
          {t("N/A")}
        </StatusTag>
      }
    />
  );
}

function ResultTagCell({
  result,
  monitorStatus,
  nextScheduledRunDate,
  details,
}: {
  result: string | null;
  monitorStatus: string | null;
  nextScheduledRunDate: string | null;
  details: DashboardApplicant["overall_last_run_details"];
}) {
  if (!result) {
    return (
      <PendingResultTag
        monitorStatus={monitorStatus}
        nextScheduledRunDate={nextScheduledRunDate}
      />
    );
  }
  return (
    <MonitoringRunResultTagWithTooltip
      result={result}
      details={details ?? []}
    />
  );
}

function checkTypeLabel(checkType: MonitoredCheckTypeSummary): string {
  return checkType.label ?? checkType.type;
}

export function MonitoringDashboardTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { params, setParam } = useQueryParams();
  const [selectedKeys, setSelectedKeys] = useState<Set<Key>>(new Set());

  const monitorStatus = params.monitor_status || "ACTIVE";
  const hasActiveFilters = Boolean(
    params.search ||
      params.check_type ||
      params.overall_result ||
      (params.monitor_status && params.monitor_status !== "ACTIVE")
  );
  const apiParams: Record<string, string | string[]> = {
    ...(params.page && { page: params.page }),
    ...(params.page_size && { page_size: params.page_size }),
    ...(params.search && { search: params.search }),
    monitor_status: monitorStatus,
    ...(params.check_type && { check_type: params.check_type.split(",") }),
    ...(params.overall_result && {
      overall_result: params.overall_result.split(","),
    }),
    ...(params.ordering && { ordering: params.ordering }),
  };
  const { data, isLoading } = useDashboardApplicants(apiParams);
  const results = useMemo(() => data?.results ?? [], [data]);
  const selectedSubjectIds = useMemo(
    () => Array.from(selectedKeys).map(String),
    [selectedKeys]
  );
  const selectedStatuses = useMemo(() => {
    const statusById = new Map(
      results.map((row) => [row.id, row.monitor_status_rollup])
    );
    return selectedSubjectIds
      .map((id) => statusById.get(id))
      .filter((status): status is string => Boolean(status));
  }, [selectedSubjectIds, results]);

  const selectionResetKey = [
    params.search,
    params.monitor_status,
    params.check_type,
    params.overall_result,
    params.page,
    params.page_size,
  ].join("|");

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [selectionResetKey]);

  function handleSelectionChange(keys: Selection) {
    if (keys === "all") {
      setSelectedKeys(new Set(results.map((row) => row.id)));
      return;
    }
    setSelectedKeys(new Set(keys));
  }

  function handleRowAction(key: Key) {
    navigate(`/monitoring/${key}`);
  }

  const columns: ColumnDefinition<DashboardApplicant>[] = [
    {
      key: "name",
      name: t("Name"),
      width: 240,
      isRowHeader: true,
      testID: "applicant-column-header",
      render(row) {
        const name = getApplicantDisplayName(row);
        return (
          <Stack gap="space-0">
            <Text style="form/body-secondary">{name}</Text>
            {row.latest_email && (
              <Text style="global/info-label">{row.latest_email}</Text>
            )}
          </Stack>
        );
      },
    },
    {
      key: "monitor_status_rollup",
      name: t("Monitoring status"),
      width: 160,
      testID: "status-column-header",
      render(row) {
        return <MonitorStatusTag status={row.monitor_status_rollup} />;
      },
    },
    {
      key: "monitored_check_types",
      name: t("Check monitored"),
      width: 220,
      testID: "checks-column-header",
      render(row) {
        if (!row.monitored_check_types?.length) {
          return <Text style="form/body-secondary">—</Text>;
        }
        return (
          <Stack gap="space-0">
            {row.monitored_check_types.map((ct) => (
              <Text key={ct.type} style="form/body-secondary">
                {checkTypeLabel(ct)}
              </Text>
            ))}
          </Stack>
        );
      },
    },
    {
      key: "overall_last_run_result",
      name: t("Overall result"),
      width: 160,
      testID: "result-column-header",
      render(row) {
        return (
          <ResultTagCell
            result={row.overall_last_run_result}
            monitorStatus={row.monitor_status_rollup}
            nextScheduledRunDate={row.next_scheduled_run_date}
            details={row.overall_last_run_details ?? []}
          />
        );
      },
    },
  ];

  return (
    <>
      <MonitoringSummaryTiles />
      <FilterBar
        hasSearch
        searchPlaceholder={t("Search by name or email")}
        testID="monitoring-dashboard"
      >
        <MonitoringCheckTypeFilter />
        <MonitoringStatusFilter />
        <MonitoringResultFilter />
      </FilterBar>
      <MonitoringBulkActionBar
        selectedSubjectIds={selectedSubjectIds}
        selectedStatuses={selectedStatuses}
        onActionComplete={() => setSelectedKeys(new Set())}
      />
      <DataTable
        columns={columns}
        rows={results}
        rowKey="id"
        isLoading={isLoading}
        selectionMode="multiple"
        selectionBehavior="toggle"
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        onRowAction={handleRowAction}
        aria-label="monitoring dashboard table"
        testId="monitoring-dashboard-table"
        renderEmptyState={() => (
          <Text style="form/body-secondary">
            {hasActiveFilters
              ? t("There are no results that match your current filter.")
              : t("You have no applicants monitored.")}
          </Text>
        )}
      />
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params.page_size ? Number(params.page_size) : 25}
      />
    </>
  );
}
